import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { TeamAPI } from '../../utils/API/TeamAPI';
import {
	DELETE_TEAM,
	FETCH_V4_TEAMS,
	SAVE_TEAM,
} from '../constants/team.constants';
import { WS_DATA_INIT } from '../constants/app.constants';
import { ADMIN_PAGE } from '../../containers/AdminPage/constants';
import { selectCurrentUser } from '../selectors/app.selectors';
import { v4ScopeFor } from '../../api/V4client';
import {
	deleteV4Team,
	fetchV4Teams,
	getV4TeamDeviceIds,
	saveV4Team,
	syncV4TeamDevices,
} from '../../containers/V4Teams/teams';
import { v4TeamsLoaded } from '../actions/team.actions';

const api = new TeamAPI();

const getField = (team, key) =>
	team && typeof team.get === 'function' ? team.get(key) : team?.[key];

/**
 * Returns true when the team should be persisted via the V4 platform.
 * V3 teams have a numeric id; V4 teams carry a `source` tag and a UUID.
 */
function isV4Team(team) {
	return getField(team, 'source') === 'v4' || !!getField(team, 'v4Data');
}

/**
 * The V4 scope for the current user's V3 account. Null when it isn't migrated
 * yet, and for V4-native sessions — those are scoped by V4client instead.
 */
function* resolveV4ScopeForCurrentUser() {
	const user = yield select(selectCurrentUser());
	if (!user?.accountId) return null;
	try {
		return yield call(() => v4ScopeFor(user.accountId));
	} catch (e) {
		return null;
	}
}

function* saveV4TeamSaga(team) {
	const scope = yield* resolveV4ScopeForCurrentUser();
	const saved = yield call(saveV4Team, team, scope);
	const teamId = saved?.id || getField(team, 'id');
	if (!teamId) return;

	// Reconcile the device link list against the previous server state. The
	// V3 UI edits a flat array; V4 needs explicit link / unlink calls per
	// device. `getV4TeamDeviceIds` reads the previous list off `v4Data.members`
	// that came back with the last `teamsList`.
	const prevDeviceIds = getV4TeamDeviceIds(team);
	const nextDeviceIds = (getField(team, 'deviceIds') || []).map(String);
	yield call(syncV4TeamDevices, teamId, nextDeviceIds, prevDeviceIds, scope);

	// Refresh the V4 team list so the UI reflects the new members payload
	// (which includes the link ids needed for any future unlink).
	yield* fetchV4TeamsForUserSaga();
}

function* saveTeam({ team }) {
	if (isV4Team(team)) {
		yield* saveV4TeamSaga(team);
		return;
	}
	if (team.id) {
		yield api.updateTeam(team);
	} else {
		yield api.newTeam(team);
	}
}

function* deleteTeam({ team }) {
	const teams = team || [];
	const v4Teams = teams.filter(isV4Team);
	const v3Teams = teams.filter(t => !isV4Team(t));

	if (v3Teams.length) {
		yield all(v3Teams.map(t => api.deleteTeam(t)));
	}
	if (v4Teams.length) {
		const scope = yield* resolveV4ScopeForCurrentUser();
		yield all(v4Teams.map(t => call(deleteV4Team, t, scope)));
		yield* fetchV4TeamsForUserSaga();
	}
}

/**
 * Fetches V4 teams for the current user's account and stores them in Redux.
 * Skipped on the admin flow — admin sub-account teams are loaded from the
 * AdminPage saga instead, in line with the device / ARC patterns.
 */
export function* fetchV4TeamsForUserSaga() {
	try {
		// On the admin flow, AdminPage handles the merged fetch — bail so we
		// don't double-load and stomp on accountName-tagged admin teams.
		const adminState = yield select(state => state[ADMIN_PAGE]);
		const rawAccounts = adminState?.get?.('accounts') || [];
		const v3Accounts = rawAccounts.toArray
			? rawAccounts.toArray()
			: Array.isArray(rawAccounts)
			? rawAccounts
			: [];
		if (v3Accounts.length) return;

		const user = yield select(selectCurrentUser());
		if (!user || (!user.accountId && !user.isV4Native)) {
			yield put(v4TeamsLoaded([]));
			return;
		}

		// V4-native has no V3 account to resolve — V4client supplies the scope.
		let v4Scope = null;
		if (!user.isV4Native) {
			try {
				v4Scope = yield call(() => v4ScopeFor(user.accountId));
			} catch (e) {
				// User's V3 account isn't migrated to V4 yet — nothing to merge.
				yield put(v4TeamsLoaded([]));
				return;
			}
		}

		const v4Teams = yield call(fetchV4Teams, v4Scope);
		yield put(v4TeamsLoaded(v4Teams));
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn('Failed to fetch V4 teams for user:', e);
		yield put(v4TeamsLoaded([]));
	}
}

export const teamSagas = [
	takeLatest(SAVE_TEAM, saveTeam),
	takeLatest(DELETE_TEAM, deleteTeam),
	takeLatest(FETCH_V4_TEAMS, fetchV4TeamsForUserSaga),
	takeLatest(WS_DATA_INIT, fetchV4TeamsForUserSaga),
];
